import { useState, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useQuery } from '@tanstack/react-query'
import { ref, off, onValue } from 'firebase/database'
import { isEmpty, debounce, isEqual } from 'lodash'
import moment from 'moment'

import database from 'firebase-config'
import { sleep } from '../../../../../../../utils/functions/helpers'

import { getAppointments } from 'components/core/services/environmentService'
import { getDispatchSettings } from 'components/core/services/environmentService'

export const currentSchedulerKeys = {
	all: ['employee-tile'],
	detailAll: (tileKey, date, range) => [...currentSchedulerKeys.all, tileKey, date, range],
	detailDevice: (tileKey, date, range, deviceKey) => [
		...currentSchedulerKeys.all,
		tileKey,
		date,
		range,
		deviceKey,
	],
}

export function useEmployeeEvent({ tileKey, rangeType, selectedDevice, selectedDateDisplay }) {
	const { environment } = useSelector((state) => state)
	const [schedulerRef, setSchedulerCountRef] = useState('')

	const [defaultSchedulerStatus, setDefaultSchedulerStatus] = useState([])
	const selectDate = moment(selectedDateDisplay).format('YYYY-MM-DD')
	const queryKey = useMemo(() => {
		if (!selectedDevice || isEmpty(selectedDevice))
			return currentSchedulerKeys.detailAll(tileKey, selectDate, rangeType)
		return currentSchedulerKeys.detailDevice(tileKey, selectDate, rangeType, selectedDevice.key)
	}, [tileKey, rangeType, selectedDevice, selectDate])

	useEffect(() => {
		getDispatchSettings(environment.apiToken)
			.then((setting) => {
				setDefaultSchedulerStatus(setting?.data?.status)
			})
			.catch((err) => {
				console.log(err)
			})
	}, [])

	const { data, isLoading, isFetching, refetch } = useQuery({
		queryKey: queryKey,
		queryFn: () =>
			getAppointments(environment.apiToken, selectDate, rangeType, selectedDevice?.key),
		enabled: Boolean(rangeType),
		staleTime: 5000,
	})

	const {
		data: defaultStatusSettings,
		isLoading: isLoadingDefaultStatusSettings,
		isFetching: isFetchingDefaultStatusSettings,
		refetch: refetchDefaultStatusSettings,
	} = useQuery({
		queryKey: [...currentSchedulerKeys.all, 'defaultStatusSettings'],
		queryFn: () => getDispatchSettings(environment.apiToken),
		enabled: Boolean(rangeType),
		staleTime: 60000,
	})

	useEffect(() => {
		const customerKey = environment.account.key
		if (!customerKey) return

		if (!isEmpty(schedulerRef)) {
			off(schedulerRef)
		}

		setSchedulerCountRef(ref(database, `userSessions/customerKey/${customerKey}`))
	}, [environment.account.key])

	const debouncedRefetch = debounce(() => {
		refetch()
	}, 500)

	useEffect(() => {
		if (isEmpty(schedulerRef)) return

		const unSubscribeSchedulerOffset = onValue(schedulerRef, (snapshot) => {
			sleep(2000).then(() => {
				debouncedRefetch()
			})
		})

		return () => {
			unSubscribeSchedulerOffset()
		}
	}, [schedulerRef])

	useEffect(() => {
		if (isLoadingDefaultStatusSettings || isFetchingDefaultStatusSettings) {
			if (isEqual(defaultStatusSettings?.data?.status, defaultSchedulerStatus)) return
			setDefaultSchedulerStatus([])
			return
		}

		if (isEqual(defaultStatusSettings?.data?.status, defaultSchedulerStatus)) return
		setDefaultSchedulerStatus(defaultStatusSettings?.data?.status)
	}, [defaultStatusSettings, isLoadingDefaultStatusSettings, isFetchingDefaultStatusSettings])

	return {
		appoinments: data,
		isLoadingData: isFetching,
		refetch,
		defaultSchedulerStatus,
	}
}
